package kz.dentalcrm.app.core.ui.components

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.horizontalDrag
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.AwaitPointerEventScope
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerInputChange
import androidx.compose.ui.input.pointer.changedToUp
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.Layout
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.onClick
import androidx.compose.ui.semantics.role
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.stateDescription
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import kz.dentalcrm.app.expectations.rememberHapticFeedback
import kz.dentalcrm.app.theme.DefaultTheme

private const val NO_SEGMENT_INDEX = -1

private val DEFAULT_HEIGHT = 40.dp
private val DEFAULT_TRACK_PADDING = 4.dp
private val DEFAULT_CORNER_RADIUS = 12.dp
private val DEFAULT_SEGMENT_HORIZONTAL_PADDING = 12.dp

@Composable
fun <T : Any> StyledSegmentedControl(
    segments: List<T>,
    selectedSegment: T,
    onSegmentSelected: (T) -> Unit,
    modifier: Modifier = Modifier,
    height: Dp = DEFAULT_HEIGHT,
    backgroundColor: Color = DefaultTheme.colors.surfaceTertiary,
    thumbColor: Color = DefaultTheme.colors.primary,
    trackPadding: Dp = DEFAULT_TRACK_PADDING,
    cornerRadius: Dp = DEFAULT_CORNER_RADIUS,
    content: @Composable (segment: T, isSelected: Boolean) -> Unit
) {
    val hapticFeedback = rememberHapticFeedback()
    val state = remember { StyledSegmentedControlState() }
    state.segmentCount = segments.size
    state.selectedSegment = segments.indexOf(selectedSegment)
    state.onSegmentSelected = { index ->
        hapticFeedback.performHapticFeedback()
        onSegmentSelected(segments[index])
    }

    val selectedIndexOffset by animateFloatAsState(
        targetValue = state.selectedSegment.toFloat(),
        animationSpec = tween(durationMillis = 200),
        label = "selected_offset"
    )

    val backgroundShape = RoundedCornerShape(cornerRadius)
    val thumbShape = RoundedCornerShape(cornerRadius - trackPadding / 2)

    Layout(
        content = {
            // Thumb
            Box(
                modifier = Modifier.background(thumbColor, thumbShape)
            )
            // Segments
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .selectableGroup()
            ) {
                segments.forEachIndexed { i, segment ->
                    val isSelected = i == state.selectedSegment
                    val isPressed = i == state.pressedSegment

                    val alpha by animateFloatAsState(
                        targetValue = if (!isSelected && isPressed) 0.6f else 1f,
                        animationSpec = tween(durationMillis = 100),
                        label = "segment_alpha"
                    )

                    val segmentModifier = Modifier.semantics(mergeDescendants = true) {
                        selected = isSelected
                        role = Role.Button
                        onClick { state.onSegmentSelected(i); true }
                        stateDescription = if (isSelected) "Selected" else "Not selected"
                    }

                    Box(
                        contentAlignment = Alignment.Center,
                        modifier = Modifier
                            .weight(1f)
                            .then(segmentModifier)
                            .padding(horizontal = DEFAULT_SEGMENT_HORIZONTAL_PADDING)
                            .alpha(alpha)
                    ) {
                        content(segment, isSelected)
                    }
                }
            }
        },
        modifier = modifier
            .fillMaxWidth()
            .height(height)
            .then(state.inputModifier)
            .background(backgroundColor, backgroundShape)
            .padding(trackPadding)
    ) { measurables, constraints ->
        val (thumbMeasurable, segmentsMeasurable) = measurables

        val segmentsPlaceable = segmentsMeasurable.measure(constraints)
        val segmentWidth = segmentsPlaceable.width / segments.size

        val thumbPlaceable = thumbMeasurable.measure(
            Constraints.fixed(
                width = segmentWidth,
                height = segmentsPlaceable.height
            )
        )

        layout(segmentsPlaceable.width, segmentsPlaceable.height) {
            thumbPlaceable.placeRelative(
                x = (selectedIndexOffset * segmentWidth).toInt(),
                y = 0
            )
            segmentsPlaceable.placeRelative(IntOffset.Zero)
        }
    }
}

/**
 * Styled segment content with customizable text appearance.
 */
@Composable
fun StyledSegmentItem(
    text: String,
    isSelected: Boolean,
    modifier: Modifier = Modifier,
    selectedTextColor: Color = DefaultTheme.colors.onPrimary,
    unselectedTextColor: Color = DefaultTheme.colors.onSurface,
    selectedStyle: TextStyle = DefaultTheme.typography.bodySmallMedium,
    unselectedStyle: TextStyle = DefaultTheme.typography.bodySmallMedium,
) {
    Text(
        text = text,
        style = if (isSelected) selectedStyle else unselectedStyle,
        color = if (isSelected) selectedTextColor else unselectedTextColor,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = modifier
    )
}

private class StyledSegmentedControlState {
    var segmentCount by mutableIntStateOf(0)
    var selectedSegment by mutableIntStateOf(0)
    var onSegmentSelected: (Int) -> Unit by mutableStateOf({})
    var pressedSegment by mutableStateOf(NO_SEGMENT_INDEX)

    val inputModifier = Modifier.pointerInput(segmentCount) {
        val segmentWidth = size.width / segmentCount

        fun segmentIndex(change: PointerInputChange): Int =
            ((change.position.x / size.width.toFloat()) * segmentCount)
                .toInt()
                .coerceIn(0, segmentCount - 1)

        awaitEachGesture {
            val down = awaitFirstDown()
            pressedSegment = segmentIndex(down)
            val downOnSelected = pressedSegment == selectedSegment
            val segmentBounds = Rect(
                left = pressedSegment * segmentWidth.toFloat(),
                right = (pressedSegment + 1) * segmentWidth.toFloat(),
                top = 0f,
                bottom = size.height.toFloat()
            )

            if (downOnSelected) {
                horizontalDrag(down.id) { change ->
                    pressedSegment = segmentIndex(change)
                    if (pressedSegment != selectedSegment) {
                        onSegmentSelected(pressedSegment)
                    }
                }
            } else {
                styledWaitForUpOrCancellation(inBounds = segmentBounds)
                    ?.let { onSegmentSelected(pressedSegment) }
            }

            pressedSegment = NO_SEGMENT_INDEX
        }
    }
}

@Suppress("ReturnCount")
private suspend fun AwaitPointerEventScope.styledWaitForUpOrCancellation(
    inBounds: Rect
): PointerInputChange? {
    while (true) {
        val event = awaitPointerEvent(PointerEventPass.Main)
        if (event.changes.all { it.changedToUp() }) {
            return event.changes[0]
        }

        if (event.changes.any { it.isConsumed || !inBounds.contains(it.position) }) {
            return null
        }

        val consumeCheck = awaitPointerEvent(PointerEventPass.Final)
        if (consumeCheck.changes.any { it.isConsumed }) {
            return null
        }
    }
}
